import React from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import type { GuideServers } from './mailboxGuideState';
import { mailboxGuideMessages as messages } from './mailboxGuideMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the guide; each part uses its own slice of it */
import styles from './MailboxGuide.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ManualServersProps {
  servers: GuideServers;
  onChange: (servers: GuideServers) => void;
}

function portOf(value: string): number | null {
  const port = Number.parseInt(value, 10);
  return Number.isNaN(port) || port < 1 || port > 65535 ? null : port;
}

interface FieldProps {
  id: string;
  label: MessageDescriptor;
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  narrow?: boolean;
}

const Field: React.FC<FieldProps> = ({ id, label, value, placeholder, onChange, narrow = false }) => {
  const intl = useIntl();
  return (
    <div className={narrow ? 'col-4' : 'col-8'}>
      <label className="form-label small fw-semibold" htmlFor={id}>
        {intl.formatMessage(label)}
      </label>
      <input
        id={id}
        className="form-control form-control-sm"
        value={value}
        placeholder={placeholder}
        inputMode={narrow ? 'numeric' : undefined}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
};

/**
 * The servers typed by hand (design outreach-v2 o4): for "Something else" when the lookup found
 * nothing. An empty port lets the server try 993 and 465, then 587.
 */
const ManualServersComponent: React.FC<ManualServersProps> = ({ servers, onChange }) => {
  const intl = useIntl();
  return (
    <>
      <div className={styles.help}>
        <b>{intl.formatMessage(messages.serversHelpTitle)}</b>
        {intl.formatMessage(messages.serversHelpBody)}
      </div>
      <div className="row g-2">
        <Field
          id="guide-imap-host"
          label={messages.imapHost}
          value={servers.imapHost}
          placeholder="imap.yourhost.com"
          onChange={(imapHost) => onChange({ ...servers, imapHost: imapHost.trim() })}
        />
        <Field
          id="guide-imap-port"
          label={messages.imapPort}
          value={servers.imapPort === null ? '' : String(servers.imapPort)}
          placeholder="993"
          narrow
          onChange={(port) => onChange({ ...servers, imapPort: portOf(port) })}
        />
        <Field
          id="guide-smtp-host"
          label={messages.smtpHost}
          value={servers.smtpHost}
          placeholder="smtp.yourhost.com"
          onChange={(smtpHost) => onChange({ ...servers, smtpHost: smtpHost.trim() })}
        />
        <Field
          id="guide-smtp-port"
          label={messages.smtpPort}
          value={servers.smtpPort === null ? '' : String(servers.smtpPort)}
          placeholder="465"
          narrow
          onChange={(port) => onChange({ ...servers, smtpPort: portOf(port) })}
        />
      </div>
      <span className="small text-body-secondary">{intl.formatMessage(messages.portHint)}</span>
    </>
  );
};

export const ManualServers = TypedMemo(ManualServersComponent);
